import { refreshTokenApi } from "@/api/services/main/auth";
import { getCustomSentryExceptionError, handleApiErrorWithCode } from "@/utils/error";
import { removeToken, verifyToken } from "@/utils/token";
import { getMatchRoutes, getQueryFromURL } from "@/utils/url";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { AppRoutes } from "@/routes/config";
import { createUrlWithParams } from "@/utils/url";
import { LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY } from "@/features/learning-progress/constants/filter";
import { EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY } from "@/features/exam-steps/constants/filter";
import { BULK_SYNC_FILTER_STORAGE_KEY } from "@/features/learner/components/learner-list/bulk-sync-filter";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const zipCodeURL = `${process.env.REACT_APP_API_ZIP_CODE}`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

// Separate axios instance for refresh token calls to avoid recursive 401 interceptor
export const refreshClient = axios.create({
  baseURL,
});

export const zipCodeApi = axios.create({
  baseURL: zipCodeURL,
});

export const clientApi = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

type QueueItem = {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  config: AxiosRequestConfig;
};

let isRefreshing = false;
let refreshQueue: QueueItem[] = [];

const redirectToLogin = () => {
  const oldUrl = `${location.pathname}${location.search}`;
  const loginUrl = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
  window.location.replace(loginUrl);
};

const processQueue = (error: unknown, newToken?: string) => {
  refreshQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(
        axios({
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${newToken}` },
        }),
      );
    }
  });
  refreshQueue = [];
};

//  in case of error
export const onError = async (error: AxiosError) => {
  if (error.response) {
    // Access Token was expired
    if (error.response.status === 401 && error.config) {
      // Skip refresh for the refresh-token endpoint itself to avoid infinite loop
      if (error.config.url?.includes("/refresh-token")) {
        return Promise.reject(error.response?.data);
      }

      if (isRefreshing) {
        // Queue request while refresh is in progress
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject, config: error.config! });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = Cookies.get("refresh_token");
        const email = Cookies.get("email");
        const ownerId = Cookies.get("ownerId");
        if (!email || !refreshToken || !ownerId) {
          processQueue(error);
          logout();
          redirectToLogin();
          return Promise.reject(error.response?.data);
        }

        // Reuse shared refreshTokenApi to avoid concurrent refresh calls
        const rs = await refreshTokenApi({ email, refreshToken, ownerId });
        const newToken = rs.data.accessToken;
        processQueue(null, newToken);

        // Retry original request with new token
        return axios({
          ...error.config,
          headers: { ...error.config.headers, Authorization: `Bearer ${newToken}` },
        });
      } catch (refreshError) {
        processQueue(refreshError);
        logout();
        redirectToLogin();
        return Promise.reject(error.response?.data);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response.status === 403) {
      Cookies.remove("match_token");
      Cookies.remove("refresh_token");
      Cookies.remove("userId");
      Cookies.remove("rsoId");
      Cookies.remove("email");
      Cookies.remove("ownerId");
      Cookies.remove("isUser");
    }
  }
  handleApiErrorWithCode(error);
  const status = error.response?.status;
  const isLoginRoute = window.location.pathname.startsWith(AppRoutes.login);
  const shouldCapture404OnLogin = status === 404 && isLoginRoute;
  const shouldCaptureServerError = !!status && status >= 500 && status !== 503;

  if (shouldCapture404OnLogin || shouldCaptureServerError) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();
  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    const matchRoutes = getMatchRoutes();
    config.headers["X-RouteName"] = matchRoutes.at(-1)?.id;

    const screenRouteUrl = new URL(window.location.href);
    screenRouteUrl.search = "";
    config.headers["X-RouteURL"] = screenRouteUrl;

    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, onError);
globalApi.interceptors.response.use(onSuccess, onError);

export const setAxiosAuth = () => {
  const token = Cookies.get("match_token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const setExternalAxiosAuth = () => {
  const query = getQueryFromURL<{ token: string }>();
  const token = query?.token;
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("match_token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

export const logout = () => {
  removeToken();
  Cookies.remove("userId");
  Cookies.remove("rsoId");
  Cookies.remove("email");
  Cookies.remove("ownerId");
  // clear filters
  localStorage.removeItem(LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY);
  localStorage.removeItem(EXAM_STEPS_ADVANCED_FILTERS_STORAGE_KEY);
  localStorage.removeItem(BULK_SYNC_FILTER_STORAGE_KEY);
};

export const lazyApi = <T = void>(fn: () => Promise<T>): [AbortController, () => Promise<T>] => {
  const controller = new AbortController();

  return [controller, fn];
};
